export function buildNotificationGuide(settings) {
    return (settings || [])
        .filter((row) => row.isActive !== false && row.isActive !== 0)
        .map((row) => ({
            type: row.notification_type || "",
            category: row.Category || row.category || "",
            severityKey:
                row.severity !== null && row.severity !== undefined
                    ? String(row.severity)
                    : "",
            severity: row.Severity || "",
            businessImpactKey:
                row.businessImpact !== null &&
                row.businessImpact !== undefined
                    ? String(row.businessImpact)
                    : "",
            businessImpact: row.Business_Impact || "",
            needsAttentionKey:
                row.attention !== null && row.attention !== undefined
                    ? String(row.attention)
                    : "",
            needsAttention: row.Attention || "",
            recommendedAction:
                row.Recommended_Action || row.recommendationAction || ""
        }));
}

export function getLookupOptions(actions, entity) {
    return (actions || []).filter((a) => a.entity === entity);
}

export function getLookupLabel(actions, entity, key) {
    if (key === null || key === undefined || key === "") {
        return "";
    }
    const match = (actions || []).find(
        (a) =>
            a.entity === entity && String(a.key) === String(key)
    );
    if (!match) {
        return String(key);
    }
    if (match.value) {
        return match.value;
    }
    if (match.description) {
        return match.description;
    }
    return String(match.key);
}

export function getClassification(type, guide) {
    const rule = (guide || []).find(
        (item) => item.type === type
    );

    if (!rule) {
        return {
            severity: "",
            businessImpact: "",
            needsAttention: "",
            recommendedAction: ""
        };
    }

    return {
        severity: rule.severity || "",
        businessImpact: rule.businessImpact || "",
        needsAttention: rule.needsAttention || "",
        recommendedAction: rule.recommendedAction || ""
    };
}

export function formatEventTime(value) {
    if (!value) {
        return "-";
    }

    return String(value)
        .replace("T", " ")
        .replace("Z", "")
        .replace(/(\.\d{2})\d*/, "$1");
}

export function getPayloadField(payload, names) {
    if (!payload) {
        return "";
    }

    if (
        !Array.isArray(payload) &&
        typeof payload === "object"
    ) {
        for (let i = 0; i < names.length; i++) {
            const key = Object.keys(payload).find(
                (item) =>
                    item.toLowerCase() ===
                    names[i].toLowerCase()
            );

            if (
                key &&
                payload[key] != null &&
                payload[key] !== ""
            ) {
                return String(payload[key]);
            }
        }

        return "";
    }

    if (!Array.isArray(payload)) {
        return "";
    }

    for (let i = 0; i < payload.length; i++) {
        const item = payload[i];

        const label = String(
            item.label || ""
        )
            .toLowerCase()
            .replace(/[:\s]+$/g, "");

        for (let j = 0; j < names.length; j++) {
            if (
                label ===
                names[j].toLowerCase()
            ) {
                return item.value != null &&
                    item.value !== ""
                    ? String(item.value)
                    : "";
            }
        }
    }

    return "";
}

export function payloadToObject(payload) {
    if (
        payload &&
        typeof payload === "object" &&
        !Array.isArray(payload)
    ) {
        return payload;
    }

    if (!Array.isArray(payload)) {
        return {};
    }

    const obj = {};

    payload.forEach((item) => {
        if (item.isHeader) {
            return;
        }

        if (
            item.label &&
            item.label !== ":"
        ) {
            obj[item.label] = item.value;
        }
    });

    return obj;
}

export function payloadSnippet(payload) {
    const obj = payloadToObject(payload);

    try {
        return JSON.stringify(obj);
    } catch (error) {
        return "";
    }
}

export function getPrimaryId(notification) {
    if (
        notification.asin &&
        String(notification.asin).trim() !== "" &&
        notification.asin !== "-"
    ) {
        return String(notification.asin).trim();
    }

    if (
        notification.sku &&
        String(notification.sku).trim() !== "" &&
        notification.sku !== "-"
    ) {
        return String(notification.sku).trim();
    }

    if (
        notification.sellerId &&
        String(notification.sellerId).trim() !== ""
    ) {
        return String(notification.sellerId).trim();
    }

    return notification.id ? String(notification.id) : "";
}

export function mapApiNotification(row, index) {
    const payload =
        row.notificationPayload || [];

    const asinRaw =
        (row.asin && row.asin !== "-"
            ? row.asin
            : "") ||
        getPayloadField(
            payload,
            ["ASIN", "asin", "Asin"]
        ) ||
        "";

    const sku =
        row.sku ||
        getPayloadField(
            payload,
            [
                "SKU",
                "sku",
                "SellerSku",
                "sellerSku"
            ]
        ) ||
        "";

    const sellerId =
        row.sellerID ||
        row.sellerId ||
        "";

    let identifierType = "SKU-only";

    if (asinRaw) {
        identifierType = "ASIN / SKU";
    } else if (sku) {
        identifierType = "SKU-only";
    } else if (sellerId) {
        identifierType = "Seller ID";
    }

    return {
        id:
            row.notificationID ||
            `${row.notificationType}-${row.eventTime}-${index}`,

        store:
            row.storeName || "",

        asin: asinRaw,

        sku,

        sellerId,

        identifierType,

        type:
            row.notificationType || "",

        payload,

        eventTime:
            formatEventTime(row.eventTime),

        eventTimeRaw:
            row.eventTime,

        status:
            row.status || "OPEN"
    };
}

export function severityBadgeClass(severity) {
    if (severity === "CRITICAL") {
        return "anm-badge-critical";
    }

    if (severity === "HIGH") {
        return "anm-badge-high";
    }

    if (severity === "MEDIUM") {
        return "anm-badge-medium";
    }

    return "anm-badge-low";
}

export function statusBadgeClass(status) {
    if (status === "RESOLVED") {
        return "anm-badge-status-resolved";
    }

    if (status === "NEW") {
        return "anm-badge-status-new";
    }

    if (status === "IN PROGRESS") {
        return "anm-badge-status-progress";
    }

    if (status === "INFORMATIONAL") {
        return "anm-badge-status-info";
    }

    return "anm-badge-status-open";
}

export function entityBadgeClass(entityType) {
    if (entityType === "Seller ID") {
        return "anm-badge-critical";
    }

    if (entityType === "ASIN / SKU") {
        return "anm-badge-status-new";
    }

    return "anm-badge-medium";
}

export function isWithinDateFilter(
    eventTimeRaw,
    filterDate
) {
    if (!filterDate) {
        return true;
    }

    if (!eventTimeRaw) {
        return false;
    }

    const date = new Date(eventTimeRaw);

    if (isNaN(date.getTime())) {
        return false;
    }

    const now = new Date();

    if (filterDate === "today") {
        return (
            date.toDateString() ===
            now.toDateString()
        );
    }

    if (filterDate === "7days") {
        const from = new Date(now);

        from.setDate(
            from.getDate() - 7
        );

        return date >= from;
    }

    return true;
}